"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.appName = exports.AuthenticatedPlayer = exports.Roles = exports.SessionUser = exports.getAuth = exports.initializeAuth = void 0;
const app_1 = __importDefault(require("firebase/compat/app"));
require("firebase/compat/auth");
const config_1 = __importDefault(require("./config"));
const auth_entities_1 = require("./auth-entities");
Object.defineProperty(exports, "SessionUser", { enumerable: true, get: function () { return auth_entities_1.SessionUser; } });
Object.defineProperty(exports, "Roles", { enumerable: true, get: function () { return auth_entities_1.Roles; } });
Object.defineProperty(exports, "AuthenticatedPlayer", { enumerable: true, get: function () { return auth_entities_1.AuthenticatedPlayer; } });
class AuthService {
    constructor(axios) {
        /**
         * Returns an access token used to identify the user to a Rafflebox service.
         *
         * @remarks
         * Returns the current token if it has not expired or if it will not expire in the next five
         * minutes. Otherwise, this will refresh the token and return a new one.
         */
        this.verifyToken = (forceRefresh = undefined) => __awaiter(this, void 0, void 0, function* () {
            const signedInUser = yield this.currentUser();
            return signedInUser ? signedInUser.getIdToken(forceRefresh) : undefined;
        });
        /**
         * Return the refresh token of the current user to be used for refreshing the token outside of the SDK
         *
         * @returns - Returns the refresh token of the current user
         */
        this.getRefreshToken = () => __awaiter(this, void 0, void 0, function* () {
            const signedInUser = yield this.currentUser();
            return signedInUser === null || signedInUser === void 0 ? void 0 : signedInUser.refreshToken;
        });
        /**
         *
         * @param forceRefresh - Forces token refresh
         * @returns - Returns the current token if it has not expired or if it will not expire in the next five minutes. Otherwise, this will refresh the token and return a new one.
         */
        this.getIdToken = (forceRefresh = undefined) => __awaiter(this, void 0, void 0, function* () {
            const signedInUser = yield this.currentUser();
            return signedInUser ? signedInUser.getIdToken(forceRefresh) : undefined;
        });
        /**
         * Signs in using `email` and `password`.
         */
        this.signIn = (email, password, params) => __awaiter(this, void 0, void 0, function* () {
            const body = {
                email,
                password,
                deviceSerialNumber: params === null || params === void 0 ? void 0 : params.deviceSerialNumber,
                deviceAPKVersion: params === null || params === void 0 ? void 0 : params.deviceAPKVersion,
                deviceAppUrl: params === null || params === void 0 ? void 0 : params.deviceAppUrl,
                deviceWebAppCommitHash: params === null || params === void 0 ? void 0 : params.deviceWebAppCommitHash
            };
            let requestConfig = {
                headers: {}
            };
            if (params === null || params === void 0 ? void 0 : params.client) {
                requestConfig = {
                    headers: {
                        client: params.client
                    }
                };
            }
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            const response = appName.includes('PlayerPortal')
                ? yield this.axios.post('/player-service/v2/token', body, requestConfig)
                : yield this.axios.post('/auth/token', body, requestConfig);
            const customToken = response.data.data.token;
            yield app_1.default.auth().signInWithCustomToken(customToken);
        });
        /**
         * Switches to an organization specified by `organizationId`. The user must be already signed in.
         */
        this.switchOrganizations = (organizationId) => __awaiter(this, void 0, void 0, function* () {
            const token = yield this.changeOrganization(organizationId);
            yield app_1.default.auth().signInWithCustomToken(token);
        });
        /**
         * Signs out the current user.
         */
        this.signOut = () => __awaiter(this, void 0, void 0, function* () {
            yield app_1.default.auth().signOut();
        });
        /**
         * Checks a verification code sent to the user by email or other out-of-band mechanism.
         */
        this.checkActionCode = (code) => __awaiter(this, void 0, void 0, function* () {
            return app_1.default.auth().checkActionCode(code);
        });
        /**
         * Obtains the Firebase custom token for a different organization specified by `organizationId` that can be later exchanged for an access token.
         * The user must be already signed in.
         */
        this.changeOrganization = (organizationId) => __awaiter(this, void 0, void 0, function* () {
            const response = yield this.axios.post(`/auth-service/v2/change-organization`, { organizationId: organizationId });
            return response.data.data.token;
        });
        /**
         *
         * @param persistance - The default is `LOCAL`
         * @returns - Sets the current user's persistence state.
         */
        this.setPersistance = (persistance = 'LOCAL') => __awaiter(this, void 0, void 0, function* () {
            return app_1.default.auth().setPersistence(persistance);
        });
        /**
         * Adds an observer for changes to the user's sign-in state.
         *
         * @remarks
         * To keep the old behavior, see {@link Auth.onIdTokenChanged}.
         *
         * @param nextOrObserver - callback triggered on change.
         * @param error - callback triggered on error.
         * @param completed - callback triggered when observer is removed.
         */
        this.onAuthStateChanged = app_1.default.auth().onAuthStateChanged.bind(app_1.default.auth());
        this.axios = axios;
    }
    /**
     * The currently signed-in user (or undefined).
     */
    sessionUser() {
        return __awaiter(this, void 0, void 0, function* () {
            const user = yield this.currentUser();
            const idTokenResult = yield (user === null || user === void 0 ? void 0 : user.getIdTokenResult());
            if (idTokenResult) {
                // eslint-disable-next-line @typescript-eslint/no-use-before-define
                return appName.includes('PlayerPortal') ? new auth_entities_1.AuthenticatedPlayer(idTokenResult) : new auth_entities_1.SessionUser(idTokenResult);
            }
            return undefined;
        });
    }
    /**
     * The currently signed-in user (or null).
     */
    currentUser() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                /**
                 * The Firebase JavaScript SDK gets reinitialized upon a WEB page refresh.
                 * Thus the returned value could be `null` even when a user is signed in.
                 * The solution to the problem is to use `onAuthStateChanged` method instead.
                 *
                 * More information about the issue could be found [here](https://github.com/firebase/firebase-js-sdk/issues/462)
                 */
                if (app_1.default.auth().currentUser) {
                    resolve(app_1.default.auth().currentUser);
                }
                const unsubscribe = app_1.default.auth().onAuthStateChanged((user) => {
                    unsubscribe();
                    resolve(user);
                }, reject);
            });
        });
    }
}
let instance;
let appName;
exports.appName = appName;
/**
 * Creates and initializes an `AuthService` instance.
 *
 * The following app names are supported:
 *  - `development`
 *  - `test`
 *  - `production`
 *  - `testPlayerPortal`
 *  - `productionPlayerPortal`
 */
const initializeAuth = (env, axios) => {
    app_1.default.initializeApp(config_1.default[env]);
    exports.appName = appName = env;
    instance = new AuthService(axios);
};
exports.initializeAuth = initializeAuth;
/**
 * Returns an `AuthService` instance.
 */
const getAuth = () => {
    return instance;
};
exports.getAuth = getAuth;
//# sourceMappingURL=data:application/json;base64,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